class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    // The drawer stylesheets load without blocking render (sections/header.liquid). The drawer is only shown once they
    // are applied; if one fails or is still missing after a few seconds, the cart page is opened instead (it links its
    // own stylesheets), so an unstyled or invisible drawer never appears. Escape or close() cancels a pending open.
    // A link has no sheet while it loads. After a failed load Chrome leaves a sheet without readable rules (reading
    // them throws, as for a cross-origin sheet), so a same-origin sheet only counts as applied when it has rules.
    const state = (link) => {
      if (!link.sheet) return 'loading';
      try { return link.sheet.cssRules.length ? 'ready' : 'failed'; } catch (e) { return new URL(link.href).origin === location.origin ? 'failed' : 'ready'; }
    };
    const links = Array.from(document.querySelectorAll('link[data-cart-drawer-css]'));
    links.forEach((link) => { if (state(link) === 'ready' && link.media !== 'all') link.media = 'all'; });
    if (links.some((link) => state(link) === 'failed')) {
      window.location.href = (window.routes && window.routes.cart_url) || '/cart';
      return;
    }
    const pending = links.filter((link) => state(link) === 'loading');
    if (pending.length) {
      if (this.pendingOpen) return;
      const attempt = (this.pendingOpen = {});
      const cancelOnEscape = (event) => { if (event.code === 'Escape') this.cancelPendingOpen(); };
      document.addEventListener('keydown', cancelOnEscape);
      attempt.cleanup = () => document.removeEventListener('keydown', cancelOnEscape);
      const ready = (link) => new Promise((resolve) => {
        if (state(link) !== 'loading') return resolve(state(link) === 'ready');
        link.addEventListener('load', () => resolve(state(link) === 'ready'), { once: true });
        link.addEventListener('error', () => resolve(false), { once: true });
        const entry = performance.getEntriesByName(link.href)[0];
        if (entry && entry.responseStatus >= 400) resolve(false);
      });
      const timeout = new Promise((resolve) => setTimeout(() => resolve(false), 6000));
      Promise.race([Promise.all(pending.map(ready)).then((states) => states.every(Boolean)), timeout]).then((ok) => {
        if (this.pendingOpen !== attempt) return;
        this.cancelPendingOpen();
        if (ok) {
          pending.forEach((link) => { link.media = 'all'; });
          this.open(triggeredBy);
        } else {
          window.location.href = (window.routes && window.routes.cart_url) || '/cart';
        }
      });
      return;
    }
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  cancelPendingOpen() {
    if (!this.pendingOpen) return;
    if (this.pendingOpen.cleanup) this.pendingOpen.cleanup();
    this.pendingOpen = null;
  }

  close() {
    this.cancelPendingOpen();
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
